import React, { useState, useEffect } from 'react';
import '../styles/app.css';
import { getSquadApiBase } from '../config/game_config';
import {
    buildSyndicateInviteLink,
    copyText,
    shareSyndicateInvite,
} from '../config/telegramLaunch';
import { trackShare } from '../game/meta/analytics';
import { IconNodes, IconSquad, IconTools } from './AtelierIcons';

const SquadLobby = ({ onSelectSquad, initialView = 'list' }) => {
    const [view, setView] = useState(
        initialView === 'create' || initialView === 'join' ? initialView : 'list'
    );
    const [squads, setSquads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [squadName, setSquadName] = useState('');
    const [joinCode, setJoinCode] = useState('');
    const [error, setError] = useState('');
    const [createdInvite, setCreatedInvite] = useState(null);

    const changeView = (v) => {
        setError('');
        setSquadName('');
        setJoinCode('');
        setView(v);
    };

    const apiBase = getSquadApiBase();

    useEffect(() => {
        fetchSquads();
    }, []);

    const getAuthHeaders = () => {
        const headers = { 'Content-Type': 'application/json' };
        const tg = window.Telegram?.WebApp;
        if (tg?.initData) {
            headers.Authorization = `tma ${tg.initData}`;
        } else {
            headers.Authorization = 'mock dev_user_local';
        }
        return headers;
    };

    const fetchSquads = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${apiBase}/info`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setSquads(data.data?.syndicates || data.data?.squads || []);
            }
        } catch (e) {
            console.error('Failed to fetch syndicates:', e);
        }
        setLoading(false);
    };

    const handleCreate = async () => {
        if (!squadName.trim()) return setError('Please enter a name.');
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${apiBase}/create`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name: squadName }),
            });
            if (res.ok) {
                const data = await res.json();
                const id = data.data.id;
                const name = data.data.name;
                setCreatedInvite({ id, name, link: buildSyndicateInviteLink(id) });
                setView('invite');
                fetchSquads();
            } else {
                setError('Failed to create syndicate.');
            }
        } catch (e) {
            setError('Network error.');
        }
        setLoading(false);
    };

    const handleJoin = async () => {
        if (!joinCode.trim()) return setError('Please enter an invite code.');
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${apiBase}/join`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ syndicateId: joinCode }),
            });
            if (res.ok) {
                const data = await res.json();
                onSelectSquad(data.data.id, data.data.name);
            } else {
                const text = await res.text();
                setError(text || 'Invalid invite code.');
            }
        } catch (e) {
            setError('Network error.');
        }
        setLoading(false);
    };

    const renderList = () => (
        <div className="atelier-lobby-panel">
            <h3 className="atelier-lobby-section-title">
                <IconSquad className="atelier-inline-ico" />
                My Syndicates
            </h3>
            {loading ? (
                <p className="atelier-lobby-muted">Loading ateliers…</p>
            ) : squads.length === 0 ? (
                <div className="atelier-empty-panel">
                    <div className="atelier-empty-glyph">⌘</div>
                    <h2>No private syndicates yet</h2>
                    <p>Create a room or join with an invite code.</p>
                </div>
            ) : (
                <ul className="atelier-lobby-list">
                    {squads.map((sq) => (
                        <li key={sq.id}>
                            <div className="atelier-lobby-item-row">
                                <button
                                    type="button"
                                    className="atelier-lobby-item"
                                    onClick={() => onSelectSquad(sq.id, sq.name)}
                                >
                                    <span className="atelier-lobby-item-name">{sq.name}</span>
                                    <span className="atelier-lobby-item-role">{sq.role}</span>
                                </button>
                                <button
                                    type="button"
                                    className="atelier-chip-btn"
                                    title="Invite friends in Telegram"
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        const result = await shareSyndicateInvite(sq.id, sq.name);
                                        trackShare(result, { source: 'lobby_list' });
                                        if (result === 'shared') {
                                            setError('Telegram share opened — pick a chat.');
                                        } else if (result === 'copied') {
                                            setError('Invite message copied.');
                                        } else {
                                            setError('Could not share invite.');
                                        }
                                    }}
                                >
                                    Invite
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <div className="atelier-lobby-actions">
                <button
                    type="button"
                    className="atelier-btn-primary"
                    onClick={() => changeView('create')}
                >
                    <IconTools className="atelier-btn-ico" />
                    Create Syndicate
                </button>
                <button
                    type="button"
                    className="atelier-btn-secondary"
                    onClick={() => changeView('join')}
                >
                    Join via Code
                </button>
            </div>

            <div className="atelier-lobby-public">
                <p>Or work a public floor:</p>
                <button
                    type="button"
                    className="atelier-chip-btn atelier-lobby-public-btn"
                    onClick={() => onSelectSquad('public-workshop-0000', 'Public Workshop')}
                >
                    <IconNodes className="atelier-inline-ico" />
                    Enter Public Workshop
                </button>
                <button
                    type="button"
                    className="atelier-chip-btn atelier-lobby-public-btn"
                    onClick={() => onSelectSquad('public-syndicate-0000', 'Public Atelier')}
                >
                    <IconNodes className="atelier-inline-ico" />
                    Enter Public Atelier
                </button>
            </div>
        </div>
    );

    const renderCreate = () => (
        <form
            className="atelier-lobby-form"
            onSubmit={(e) => {
                e.preventDefault();
                handleCreate();
            }}
        >
            <h3 className="atelier-lobby-section-title">Create a Syndicate</h3>
            <p className="atelier-lobby-muted">Private room · max 8 masons · shared daily mystery</p>
            <label className="atelier-lobby-label" htmlFor="syndicate-name">
                Syndicate name
            </label>
            <input
                id="syndicate-name"
                className="atelier-lobby-input"
                type="text"
                placeholder="e.g. Pixel Pack"
                value={squadName}
                onChange={(e) => setSquadName(e.target.value)}
                maxLength={30}
            />
            {error && <p className="atelier-lobby-error">{error}</p>}
            <div className="atelier-lobby-actions">
                <button type="submit" className="atelier-btn-primary" disabled={loading}>
                    Create
                </button>
                <button
                    type="button"
                    className="atelier-btn-secondary"
                    onClick={() => changeView('list')}
                    disabled={loading}
                >
                    Cancel
                </button>
            </div>
        </form>
    );

    const renderJoin = () => (
        <form
            className="atelier-lobby-form"
            onSubmit={(e) => {
                e.preventDefault();
                handleJoin();
            }}
        >
            <h3 className="atelier-lobby-section-title">Join a Syndicate</h3>
            <p className="atelier-lobby-muted">Enter the invite code from your syndicate leader.</p>
            <label className="atelier-lobby-label" htmlFor="syndicate-code">
                Invite code
            </label>
            <input
                id="syndicate-code"
                className="atelier-lobby-input"
                type="text"
                placeholder="Invite code"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
            />
            {error && <p className="atelier-lobby-error">{error}</p>}
            <div className="atelier-lobby-actions">
                <button type="submit" className="atelier-btn-primary" disabled={loading}>
                    Join
                </button>
                <button
                    type="button"
                    className="atelier-btn-secondary"
                    onClick={() => changeView('list')}
                    disabled={loading}
                >
                    Cancel
                </button>
            </div>
        </form>
    );

    const renderInvite = () => (
        <div className="atelier-lobby-form">
            <h3 className="atelier-lobby-section-title">Syndicate ready</h3>
            <p className="atelier-lobby-muted">
                Invite friends to <strong>{createdInvite?.name}</strong> with a Telegram message
                (includes a short explanation + join link).
            </p>
            <label className="atelier-lobby-label" htmlFor="invite-code">
                Invite code (manual join)
            </label>
            <input
                id="invite-code"
                className="atelier-lobby-input"
                type="text"
                readOnly
                value={createdInvite?.id || ''}
                onFocus={(e) => e.target.select()}
            />
            {error && <p className="atelier-lobby-error">{error}</p>}
            <div className="atelier-lobby-actions">
                <button
                    type="button"
                    className="atelier-btn-primary"
                    onClick={async () => {
                        const result = await shareSyndicateInvite(
                            createdInvite?.id,
                            createdInvite?.name
                        );
                        trackShare(result, { source: 'lobby_created' });
                        if (result === 'shared') {
                            setError('Pick a Telegram chat to send the invite.');
                        } else if (result === 'copied') {
                            setError('Invite message copied to clipboard.');
                        } else {
                            setError('Share failed — try Copy message.');
                        }
                    }}
                >
                    Invite friends in Telegram
                </button>
                <button
                    type="button"
                    className="atelier-btn-secondary"
                    onClick={async () => {
                        const link =
                            createdInvite?.link || buildSyndicateInviteLink(createdInvite?.id);
                        const msg = `Join my Voxel Quest syndicate “${createdInvite?.name}”!\n${link}`;
                        const ok = await copyText(msg);
                        setError(ok ? 'Invite message copied.' : 'Could not copy.');
                    }}
                >
                    Copy invite message
                </button>
                <button
                    type="button"
                    className="atelier-btn-secondary"
                    onClick={() => onSelectSquad(createdInvite.id, createdInvite.name)}
                >
                    Enter floor
                </button>
            </div>
        </div>
    );

    return (
        <div className="atelier-lobby">
            <header className="atelier-lobby-head">
                <div className="atelier-brand atelier-brand--compact">
                    <span className="atelier-brand-mark" aria-hidden>
                        <IconSquad />
                    </span>
                    <div>
                        <div className="atelier-brand-name">Voxel Atelier</div>
                        <div className="atelier-brand-sub">Syndicate Lobby</div>
                    </div>
                </div>
            </header>
            <h1 className="atelier-overlay-title">Syndicate Lobby</h1>
            <p className="atelier-overlay-sub">Pick a private atelier or join the public floor</p>
            <div className="atelier-lobby-body">
                {view === 'list' && renderList()}
                {view === 'create' && renderCreate()}
                {view === 'join' && renderJoin()}
                {view === 'invite' && createdInvite && renderInvite()}
            </div>
        </div>
    );
};

export default SquadLobby;
